import React, { useEffect, useState } from "react";
import {
  Grid,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
} from "@mui/material";
import { DirectionsCar, Person, Add, Description } from "@mui/icons-material";
import { toast } from "react-toastify";
import useHttp from "../../Hooks/use-http";
import { useHistory } from "react-router-dom";
import VehicleRegistrationDocument from "./VehicleRegistrationDocument";

const RegisterVehicle = () => {
  const [step, setStep] = useState(1);
  const [partnerList, setPartnerList] = useState([]);
  const [formValues, setFormValues] = useState({
    partnerselected: "",
    partnerEmailID: "",
    partnerFullname: "",
    partnerPassword: "",
    partnerMobileNumber: "",
    regNumber: "",
    color: "",
    model: "",
    maker: "",
    vehicleType: "",
    fuelType: "",
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [vehicleDocuments, setVehicleDocuments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { sendRequest, isLoading } = useHttp();
  const isCustomPartner = formValues.partnerselected === "custom";
  const history = useHistory();

  useEffect(() => {
    sendRequest(
      {
        url: "/partners",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: {
          emailID: localStorage.getItem("user"),
        },
      },
      fetchPartners
    );
  }, []);

  const fetchPartners = async (data) => {
    try {
      if (data.status === 200 || data.status === 201) {
        setPartnerList(data.partners || []);
      }
    } catch (error) {
      console.error("Error fetching partner details:", error);
      toast.error("Failed to load partner details");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handlePartnerSelect = (e) => {
    const selected = e.target.value;
    const partner = partnerList.find((p) => p.emailID === selected);

    setFormValues((prev) => ({
      ...prev,
      partnerselected: selected,
      partnerEmailID: selected === "custom" ? "" : selected,
      partnerFullname: selected === "custom" ? "" : partner?.PartnerName || "",
      partnerMobileNumber:
        selected === "custom" ? "" : partner?.mobileNumber || "",
    }));
  };

  const handleNext = () => {
    // Basic validation before proceeding
    if (step === 1) {
      if (!formValues.regNumber || !formValues.model || !formValues.maker) {
        setSnackbarMessage("Please fill in all required vehicle details");
        setSnackbarOpen(true);
        return;
      }
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);

    try {
      // First register the vehicle
      const vehiclePayload = {
        ...formValues,
        partnerEmailID: isCustomPartner
          ? formValues.partnerEmailID
          : formValues.partnerselected,
      };

      const vehicleResponse = await sendRequest({
        url: "/registervehicle",
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: { data: vehiclePayload },
      });

      if (vehicleResponse.status !== 200 && vehicleResponse.status !== 201) {
        throw new Error("Vehicle registration failed");
      }

      // Then upload documents if any
      if (vehicleDocuments.length > 0) {
        const documentPayload = vehicleDocuments.map((doc) => ({
          documentNumber: doc.number,
          documentURL: doc.url,
          category: doc.category,
          isVerified: doc.required ? 0 : null,
          createdOn: new Date(),
          documentID: doc.documentID,
          numberPlate: formValues.regNumber,
        }));

        const docResponse = await sendRequest({
          url: "/addupdatedocument",
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: {
            emailID: localStorage.getItem("user"),
            data: documentPayload,
          },
        });

        if (docResponse.status !== 200 && docResponse.status !== 201) {
          throw new Error("Document upload failed");
        }
      }

      setSnackbarMessage("Vehicle registered successfully!");
      setSnackbarOpen(true);
      history.push("/vehicles");
    } catch (error) {
      setSnackbarMessage(error.message || "Registration failed");
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
      <Paper
        elevation={3}
        sx={{
          p: 4,
          borderRadius: 2,
          width: "100%",
          maxWidth: 800,
          boxShadow: "0px 5px 20px rgba(0, 0, 0, 0.1)",
        }}
      >
        <Typography
          variant="h5"
          align="center"
          gutterBottom
          sx={{ color: "primary.main", fontWeight: 600 }}
        >
          Vehicle Registration
        </Typography>

        {/* Progress Indicator */}
        <Box display="flex" justifyContent="center" sx={{ mb: 4 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: step >= 1 ? "primary.main" : "grey.300",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              mr: 1,
            }}
          >
            1
          </Box>
          <Box
            sx={{
              width: 60,
              height: 2,
              bgcolor: step >= 2 ? "primary.main" : "grey.300",
              alignSelf: "center",
              mx: 1,
            }}
          />
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              bgcolor: step >= 2 ? "primary.main" : "grey.300",
              color: "white",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            2
          </Box>
        </Box>

        {/* Step 1: Vehicle Details */}
        {step === 1 && (
          <>
            {/* Partner Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <Person
                  sx={{ mr: 1, fontSize: "1.2rem", color: "text.secondary" }}
                />
                Partner Information
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Select Partner</InputLabel>
                    <Select
                      name="partnerselected"
                      value={formValues.partnerselected}
                      onChange={handlePartnerSelect}
                      label="Select Partner"
                      variant="standard"
                    >
                      {partnerList.map((partner) => (
                        <MenuItem key={partner.emailID} value={partner.emailID}>
                          {partner.fullName}
                        </MenuItem>
                      ))}
                      <MenuItem value="custom">
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Add sx={{ mr: 1, fontSize: "1rem" }} />
                          Register New Partner
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>

              {isCustomPartner && (
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Partner Full Name *"
                      name="partnerFullname"
                      value={formValues.partnerFullname}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Partner Email *"
                      name="partnerEmailID"
                      value={formValues.partnerEmailID}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      type="email"
                      required
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Password *"
                      name="partnerPassword"
                      value={formValues.partnerPassword}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      type="password"
                      required
                      variant="standard"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Mobile Number *"
                      name="partnerMobileNumber"
                      value={formValues.partnerMobileNumber}
                      onChange={handleChange}
                      fullWidth
                      margin="normal"
                      required
                      variant="standard"
                    />
                  </Grid>
                </Grid>
              )}
            </Box>

            {/* Vehicle Section */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle1"
                sx={{ mb: 2, display: "flex", alignItems: "center" }}
              >
                <DirectionsCar
                  sx={{ mr: 1, fontSize: "1.2rem", color: "text.secondary" }}
                />
                Vehicle Details
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Registration Number *"
                    name="regNumber"
                    value={formValues.regNumber}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    placeholder="e.g. MH01AB1234"
                    required
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Color *"
                    name="color"
                    value={formValues.color}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    required
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Model *"
                    name="model"
                    value={formValues.model}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    placeholder="e.g. Swift Dzire"
                    required
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Maker *"
                    name="maker"
                    value={formValues.maker}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    placeholder="e.g. Maruti Suzuki"
                    required
                    variant="standard"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Vehicle Type</InputLabel>
                    <Select
                      name="vehicleType"
                      value={formValues.vehicleType}
                      onChange={handleChange}
                      label="Vehicle Type"
                      required
                      variant="standard"
                    >
                      <MenuItem value="Basic">Basic</MenuItem>
                      <MenuItem value="Comfort">Comfort</MenuItem>
                      <MenuItem value="Comfort Plus">Comfort Plus</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Fuel Type</InputLabel>
                    <Select
                      name="fuelType"
                      value={formValues.fuelType}
                      onChange={handleChange}
                      label="Fuel Type *"
                      required
                      variant="standard"
                    >
                      <MenuItem value="Petrol">Petrol</MenuItem>
                      <MenuItem value="Diesel">Diesel</MenuItem>
                      <MenuItem value="CNG">CNG</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={isLoading}
              >
                Next
              </Button>
            </Box>
          </>
        )}

        {/* Step 2: Document Upload */}
        {step === 2 && (
          <>
            <VehicleRegistrationDocument
              vehicleNumber={formValues.regNumber}
              documents={vehicleDocuments}
              setDocuments={setVehicleDocuments}
              isUploading={isLoading}
              setIsUploading={setIsUploading} // Make sure this is passed
              setSnackbarOpen={setSnackbarOpen}
              setSnackbarMessage={setSnackbarMessage}
              emailID={localStorage.getItem("user")}
              vehicleCustom={isCustomPartner}
            />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}
            >
              <Button variant="outlined" onClick={handleBack} sx={{ px: 4 }}>
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{ px: 4 }}
                startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
              >
                {isSubmitting ? "Submitting..." : "Complete Registration"}
              </Button>
            </Box>
          </>
        )}
      </Paper>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarMessage.includes("failed") ? "error" : "success"}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RegisterVehicle;
